import { ChangeEvent, useEffect, useRef, useState } from "react";
import clsx from "clsx";

import styles from "./DocumentDeliverySelection.module.scss";
import { Paper, Paperless } from "./svg";
import { mt } from "../../../../../../utils/tracking";

export type DocumentDeliveryValue = "mail" | "paperless";

export type DocumentDeliverySelectionState = {
  /** The selected document delivery option. */
  selectedOption: DocumentDeliveryValue;

  /** The email entered into the paperless email textbox. */
  email?: string;

  /** Whether the email has changed from the default value. */
  emailChanged: boolean;

  /** Whether the email input is valid. */
  emailValid: boolean;
};

type DocumentDeliverySelectionProps = {
  /** The option that will be initially selected. */
  initialOption: DocumentDeliveryValue;

  /** Default value for the email textbox. */
  defaultEmail?: string;

  /** ID of the paperless terms and conditions modal. */
  termsModalId: string;

  /** Callback for listening to state changes. */
  onChange: (state: DocumentDeliverySelectionState) => void;
};

export const DocumentDeliverySelection = ({
  initialOption,
  defaultEmail,
  termsModalId,
  onChange,
}: DocumentDeliverySelectionProps) => {
  const mailOptionRef = useRef<HTMLElement>(null);
  const paperlessOptionRef = useRef<HTMLElement>(null);
  const [state, setState] = useState<DocumentDeliverySelectionState>({
    selectedOption: initialOption,
    email: defaultEmail,
    emailChanged: false,
    emailValid: !!defaultEmail,
  });

  useEffect(() => {
    const ref = initialOption === "mail" ? mailOptionRef : paperlessOptionRef;
    ref.current?.click();
  }, []);

  useEffect(() => onChange(state), [state, onChange]);

  // The html and classes used here are from the HDS radio button block element.
  return (
    <fieldset
      className={clsx(
        styles["document-delivery"],
        "form-element hg-radio-button"
      )}
    >
      <legend className={clsx(styles.title, "form-element__label")}>
        Select delivery method:
      </legend>
      <div className="form-element__control">
        <span className="hg-radio-button hg-radio-button__block">
          <input
            id="document-delivery-select-1"
            name="document-delivery-select-group"
            type="radio"
            value="mail"
            onChange={onRadioChanged}
          />
          <label
            className="hg-radio-button__label"
            htmlFor="document-delivery-select-1"
          >
            <span
              className={clsx(
                styles["radio-button"],
                "hg-radio-button__checkmark"
              )}
              aria-hidden="true"
              ref={mailOptionRef}
            />
            <span className="form-element__labels">
              <span className="form-element__label">
                <img
                  src={Paper}
                  className={styles["option-icon"]}
                  aria-hidden="true"
                />
                Paper only
              </span>
              <span
                className={clsx(
                  styles["option-description"],
                  "form-element__label-description"
                )}
              >
                Receive all paper bills and documents by mail.
              </span>
            </span>
          </label>
        </span>
        <span className="hg-radio-button hg-radio-button__block">
          <input
            id="document-delivery-select-2"
            name="document-delivery-select-group"
            type="radio"
            value="paperless"
            onChange={onRadioChanged}
          />
          <label
            className="hg-radio-button__label"
            htmlFor="document-delivery-select-2"
          >
            <span
              className={clsx(
                styles["radio-button"],
                "hg-radio-button__checkmark"
              )}
              aria-hidden="true"
              ref={paperlessOptionRef}
            />
            <span className="form-element__labels">
              <span className="form-element__label">
                <img
                  src={Paperless}
                  className={styles["option-icon"]}
                  aria-hidden="true"
                />
                Enroll in paperless documents and notifications
              </span>
              <span
                className={clsx(
                  styles["option-description"],
                  "form-element__label-description"
                )}
              >
                Enjoy the convenience of paperless communication by receiving
                billing alerts and securely accessing documents online.
              </span>
              {state.selectedOption === "paperless" && (
                <>
                  <div
                    className={clsx(
                      "form-element form-element_small",
                      !state.emailValid && "has-error"
                    )}
                  >
                    <label
                      htmlFor="paperless-email"
                      className={styles["email-label"]}
                    >
                      Email address
                    </label>
                    <div className="form-element__control">
                      <input
                        id="paperless-email"
                        className="text-input"
                        type="email"
                        pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}"
                        required
                        defaultValue={state.email ?? defaultEmail}
                        onChange={(event) => {
                          setState({
                            ...state,
                            email: event.target.value,
                            emailChanged: true,
                            emailValid: event.target.checkValidity(),
                          });
                        }}
                      />
                    </div>
                    {!state.emailValid && (
                      <div className="form-element__help">
                        Please enter a valid email address.
                      </div>
                    )}
                  </div>
                  <div className={styles["paperless-terms"]}>
                    I understand and agree to the paperless{" "}
                    <button
                      className={styles["terms-button"]}
                      id={termsModalId}
                      onClick={() =>
                        mt.trackPopup("paperless terms and conditions")
                      }
                    >
                      terms and conditions
                    </button>
                    , and confirm that the above email address is to be used for
                    insurance information.
                  </div>
                </>
              )}
            </span>
          </label>
        </span>
      </div>
    </fieldset>
  );

  function onRadioChanged(event: ChangeEvent<HTMLInputElement>): void {
    const selectedOption = event.target.value as DocumentDeliveryValue;

    if (event.target.checked) {
      setState({
        ...state,
        selectedOption: selectedOption,
      });

      mt.trackAction({
        actionType: "radio",
        actionName: selectedOption,
      });
    }
  }
};
